import React from 'react';
import { AlertOpportunity, Link, UtilityText, theme } from '@policygenius/mortar';
import { useFormContext } from 'context/FormContext';
import {
  Accelerated,
  BadgeContainer,
  CardButtonsContainer,
  CarrierLink,
  CarrierProductHealthPrice,
  DesktopCardButtonsContainer,
  EappBanner,
  FinalExpense,
  Instant,
  Label,
  MobileBadgeContainer,
  MobilePrice,
  NonMed,
  ProductCardContainer,
  ProductHealthContainer,
  ProductHealthSection,
  Simplified,
  TabletUpPremium,
  TitlePrice,
} from './styles';
import { useNavigate } from 'react-router-dom';
import Premium from '../Premium';
import { carrierLinks } from 'pages/Quotes/utils';
import { DROP_TICKET_ROUTE, EAPP_ROUTE } from 'routes/constants';
import Button from 'components/Button';
import { Props } from './types';

const EAPP_CARRIER_PRODUCTS = ['trendsetter super', 'trendsetter lb'];
const EAPP_CARRIERS = ['transamerica'];

/* eslint-disable complexity */
const Card: React.FC<Props> = ({ eappEnabled, isBOR, policy }) => {
  const {
    carrierName,
    monthlyPremium,
    annualPremium,
    planIdentifierName,
    healthCategory,
    name,
    carrier,
    ...rest
  } = policy;

  const { state, dispatch } = useFormContext();
  const navigate = useNavigate();

  const acceleratedBadge = policy.attributes.includes('accelerated');
  const nonmMedBadge = policy.attributes.includes('non-med');
  const simplifiedBadge = policy.attributes.includes('simplified');
  const instantBadge = policy.attributes.includes('instant');
  const finalExpenseBadge = policy.attributes.includes('final-expense');

  const hasBadge = policy.attributes.length > 0;

  const monthly = state.paymentMode === 'Monthly';

  const premiumAmount = monthly ? monthlyPremium : annualPremium;

  const frequency = monthly ? '/mo' : '/yr';

  const carrierLink = carrierLinks[carrier.toLowerCase()];

  const isEappProductAndCarrier =
    EAPP_CARRIER_PRODUCTS.includes(planIdentifierName.toLowerCase()) &&
    EAPP_CARRIERS.includes(carrier.toLowerCase());

  const isEappEligible = eappEnabled && isEappProductAndCarrier && isBOR;

  const saveSelection = (isEapp: boolean) => {
    dispatch({
      type: 'SELECT_POLICY',
      payload: {
        carrierLink,
        annualPremium,
        carrierName,
        monthlyPremium,
        planName: name,
        planIdentifierName,
        healthCategory,
        carrier,
        ...rest,
      },
    });

    if (isEapp) {
      navigate(EAPP_ROUTE);
    } else {
      navigate(DROP_TICKET_ROUTE, { state: { keepClientState: true } });
    }
  };

  return (
    <>
      {isEappEligible && (
        <EappBanner>
          <AlertOpportunity color="green400" />
          <UtilityText weight="bold" size="small" color="green">
            eApp available — no phone call required
          </UtilityText>
        </EappBanner>
      )}
      <ProductCardContainer hasBadge={hasBadge} isEapp={isEappEligible}>
        <CarrierProductHealthPrice>
          <TitlePrice>
            <div>
              <Link
                href={carrierLink}
                variant="secondary"
                target="_blank"
                rel="noreferrer"
                color="inherit"
              >
                <CarrierLink weight="bold" size="large" text-decoration-line="underline">
                  {carrierName}
                </CarrierLink>
              </Link>
              <BadgeContainer>
                {acceleratedBadge && <Accelerated mobile={false} />}
                {instantBadge && <Instant mobile={false} />}
                {simplifiedBadge && <Simplified mobile={false} />}
                {nonmMedBadge && <NonMed mobile={false} />}
              </BadgeContainer>
            </div>
            <MobilePrice>
              <Premium premiumAmount={premiumAmount} frequency={frequency} />
            </MobilePrice>
          </TitlePrice>
          <BadgeContainer>
            {acceleratedBadge && (
              <MobileBadgeContainer>
                <Accelerated mobile />
              </MobileBadgeContainer>
            )}
            {instantBadge && (
              <MobileBadgeContainer>
                <Instant mobile />
              </MobileBadgeContainer>
            )}
            {simplifiedBadge && (
              <MobileBadgeContainer>
                <Simplified mobile />
              </MobileBadgeContainer>
            )}
            {nonmMedBadge && (
              <MobileBadgeContainer>
                <NonMed mobile />
              </MobileBadgeContainer>
            )}
            {finalExpenseBadge && (
              <MobileBadgeContainer>
                <FinalExpense mobile />
              </MobileBadgeContainer>
            )}
          </BadgeContainer>
          <ProductHealthContainer>
            <ProductHealthSection>
              <Label>Product name</Label>
              <UtilityText size="small">{planIdentifierName}</UtilityText>
            </ProductHealthSection>
            <ProductHealthSection>
              <Label>Health class</Label>
              <UtilityText size="small">{healthCategory}</UtilityText>
            </ProductHealthSection>
          </ProductHealthContainer>
          <TabletUpPremium data-testid="priceAmount">
            <Premium premiumAmount={premiumAmount} frequency={frequency} />
          </TabletUpPremium>
          <DesktopCardButtonsContainer renderEapp={isEappEligible}>
            {isEappEligible && (
              <Button
                variant="filled"
                analyticsText="eApp"
                onClick={() => saveSelection(true)}
                fullWidth={window.innerWidth < theme.breakpoints.medium}
              >
                <span>eApp</span>
              </Button>
            )}
            <Button
              variant="filled"
              analyticsText="Select"
              onClick={() => saveSelection(false)}
              fullWidth={window.innerWidth < theme.breakpoints.medium}
            >
              Drop ticket
            </Button>
          </DesktopCardButtonsContainer>
        </CarrierProductHealthPrice>
        <CardButtonsContainer renderEapp={isEappEligible}>
          {isEappEligible && (
            <Button
              variant="filled"
              analyticsText="eApp"
              onClick={() => saveSelection(true)}
              fullWidth={window.innerWidth < theme.breakpoints.medium}
            >
              <AlertOpportunity />
              <span>eApp</span>
            </Button>
          )}
          <Button
            variant="filled"
            analyticsText="Select"
            onClick={() => saveSelection(false)}
            fullWidth={window.innerWidth < theme.breakpoints.medium}
          >
            Drop ticket
          </Button>
        </CardButtonsContainer>
      </ProductCardContainer>
    </>
  );
};

export default Card;
